import { ApiError, createApiClient } from '../shared/http/ApiClient.js';
import type { LoaderResult, RouteLoader } from '../shared/ssr/RouteLoader.js';
import { notFoundResult } from '../notfound/notfound.loader.js';
import type { NotFoundData } from '../notfound/notfound.loader.js';
import { headDeNota } from './nota.head.js';
import { NotaRepository } from './models/repositories/NotaRepository.js';
import type { NotaData } from './models/entities/NotaData.js';
import { CACHE_TAG, articleTag, categoryTag } from '@ahora-patagonia/contracts';

export const NOTA_ROUTE_KEY = 'nota';

const YEAR_RE = /^\d{4}$/;
const MONTH_RE = /^(0[1-9]|1[0-2])$/;

/**
 * Loader de la nota — la pantalla más visitada: el lector llega de WhatsApp, Facebook o
 * Google directo acá, así que el HTML del server tiene que salir completo (contenido, OG,
 * `NewsArticle`).
 *
 * La dirección de una nota publicada **no cambia nunca**: un año/mes que no es el canónico
 * responde **301** a la dirección buena, y una nota archivada (o inexistente, o borrador)
 * es el mismo 404 con datos.
 */
export const notaLoader: RouteLoader<NotaData | NotFoundData> = async (ctx) => {
  const { year = '', month = '', urlKey = '' } = ctx.params;
  if (!YEAR_RE.test(year) || !MONTH_RE.test(month) || !urlKey) return notFoundResult(ctx);

  const api = createApiClient({
    baseUrl: process.env['SSR_API_URL'] ?? 'http://localhost:3000',
    headers: { host: ctx.headers['host'] ?? '' },
  });

  let data: NotaData;
  try {
    data = await new NotaRepository(api).porDireccion(year, month, urlKey);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return notFoundResult(ctx);
    throw err;
  }

  // 301 a la canónica: la API resuelve por urlKey y devuelve el path bueno.
  const pedida = `/${year}/${month}/${urlKey}`;
  if (data.nota.path !== pedida) {
    return {
      data,
      head: { title: data.nota.title },
      cache: { sMaxAge: 3600, visibility: 'public' },
      status: 301,
      redirectTo: data.nota.path,
    } satisfies LoaderResult<NotaData>;
  }

  const nota = data.nota;

  return {
    data,
    head: headDeNota(nota, ctx.site),
    cache: {
      sMaxAge: 300,
      staleWhileRevalidate: 600,
      tags: [
        articleTag(nota.id),
        ...nota.categories.map((c) => categoryTag(c.id)),
        CACHE_TAG.shell,
        CACHE_TAG.ads,
      ],
      visibility: 'public',
    },
  };
};
